import { ReactNode, ReactText } from 'react'
import {
  Box,
  CloseButton,
  Flex,
  Icon,
  useColorModeValue,
  Drawer,
  DrawerContent,
  useDisclosure,
  BoxProps,
  FlexProps
} from '@chakra-ui/react'
import {
  FiFilePlus,
  FiTrello,
  FiUsers,
  FiSmile
} from 'react-icons/fi'
import { IconType } from 'react-icons'
import { NavLink } from 'react-router-dom'
import logo from '../assets/logo.svg'

interface LinkItemProps {
  name: string;
  icon: IconType;
  url: string
}
const LinkItems: Array<LinkItemProps> = [
  { name: 'Proposals', icon: FiFilePlus, url: '/proposals' },
  { name: 'Projects', icon: FiTrello, url: '/projects' },
  { name: 'Resources', icon: FiUsers, url: '/resources' },
  { name: 'Clients', icon: FiSmile, url: '/clients' }
]

export default function SimpleSidebar ({ children }: { children: ReactNode }) {
  const { isOpen, onClose } = useDisclosure()
  return (
    <Box minH="100vh" bg={useColorModeValue('gray.50', 'gray.900')}>
      <SidebarContent
        onClose={() => onClose}
        display={{ base: 'none', md: 'block' }}
      />
      <Drawer
        autoFocus={false}
        isOpen={isOpen}
        placement="left"
        onClose={onClose}
        returnFocusOnClose={false}
        onOverlayClick={onClose}
        size="full">
        <DrawerContent>
          <SidebarContent onClose={onClose} />
        </DrawerContent>
      </Drawer>
      <Box ml={{ base: 0, md: 60 }} p="4">
        {children}
      </Box>
    </Box>
  )
}

interface SidebarProps extends BoxProps {
  onClose: () => void;
}

const SidebarContent = ({ onClose, ...rest }: SidebarProps) => {
  return (
    <Box
      bg={useColorModeValue('gray.50', 'gray.900')}
      borderRight="1px"
      borderRightColor={useColorModeValue('gray.200', 'gray.700')}
      w={{ base: 'full', md: 60 }}
      pos="fixed"
      h="full"
      {...rest}>
      <Flex h="20" alignItems="center" mx="8" justifyContent="space-between">
        <Flex>
          <img width="60px" alt="logo" src={logo} />
        </Flex>
        <CloseButton display={{ base: 'flex', md: 'none' }} onClick={onClose} />
      </Flex>
      {LinkItems.map((link) => (
        <NavItem key={link.name} url={link.url} icon={link.icon}>
          {link.name}
        </NavItem>
      ))}
    </Box>
  )
}

interface NavItemProps extends FlexProps {
  icon: IconType;
  children: ReactText;
  url: string
}
const NavItem = ({ icon, url, children, ...rest }: NavItemProps) => {
  return (
    <NavLink
      style={({ isActive }) => ({
        display: 'block',
        background: isActive ? 'purple' : 'transparent',
        padding: 4,
        margin: 4,
        borderRadius: 6
      })}
      to={url}>
      <Flex
        align="center"
        p="4"
        mx="4"
        borderRadius="lg"
        role="group"
        cursor="pointer"
        {...rest}>
        {icon && (
          <Icon
            mr="4"
            fontSize="16"
            _groupHover={{
              color: 'white'
            }}
            as={icon}
          />
        )}
        {children}
      </Flex>
    </NavLink>
  )
}
